/**
 * The quote builder's header: the phone's own bar, or the page bar with its
 * tabs and actions everywhere else (moved out of `pages/quote-builder-v2.tsx`
 * at step 8).
 *
 * Folder 52, Board 2 (`docs/job-stages.md` §1, §4): the bar's buttons are
 * `jobActions()`'s for this quote — the secondary white, the primary green
 * right-most, ⋯ beside it — drawn by `quote-stage.tsx`. The tag sits beside
 * the title and the property's name after it is a link to the property
 * (walkthrough findings §6). On a phone the header is back · title · eye · ⋯,
 * the ⋯ listing the secondary and the rest; the primary is the sticky bar's.
 */
import { PageBar } from '../frame/page-bar.js'
import { IconChevronLeft, IconEye } from '../frame/icons.js'
import type { QuoteBuilder, Tab } from '../builder/adapters/quote.js'
import { QuoteChip, StageBar, StageMore, phoneStageItems, type QuoteStage } from './quote-stage.js'

export function QuoteHeader({ qb, stage }: { qb: QuoteBuilder; stage: QuoteStage }) {
  const {
    navigate, phone, quote, busy, tab, openVisitId,
    openPhoneVisit, openTab, blocking,
  } = qb
  if (!quote) return null

  /* Who it is for: the property's company, else the contact the proposal is prepared for. */
  const whom = quote.accountName ?? quote.closed?.preparedFor ?? null
  /* The property's name is a link to the property (findings §6). */
  const place = quote.propertyName && quote.propertyId
    ? (
      <button
        type="button"
        data-property-link=""
        onClick={() => navigate(`/properties/${quote.propertyId}`)}
        className="whitespace-nowrap hover:text-text hover:underline"
      >
        {quote.propertyName}
      </button>
    )
    : quote.propertyName
  const sub = (
    <span className="truncate">
      {place}
      {place && whom ? ' · ' : null}
      {whom}
    </span>
  )

  return (
    <>
    {/*
      * §7's phone header. `PageBar` is built for a 1440-point row —
      * breadcrumbs, a title, a sub, a status and two actions — and at 390
      * points it wrapped the title over three lines and pushed Send off the
      * right edge. The phone says the same things in the order a phone can
      * read them, and its tabs are at the bottom, where a thumb is.
      */}
    {phone
      ? (
        <header className="flex shrink-0 items-start gap-2 border-b border-line bg-surface
                           px-4 py-2.5">
          {/*
            * One back arrow, always meaning "up one": to the quote from a
            * visit, to the job from the quote. Two back links on one screen
            * — a chevron and a text link four lines below it — is two
            * questions about where you are.
            */}
          <button
            type="button"
            aria-label={openVisitId ? 'Back to the quote' : 'Back to the job'}
            onClick={() => (openVisitId
              ? openPhoneVisit(null)
              : navigate(`/jobs/${quote.jobId}`))}
            className="-ml-1.5 flex h-9 w-9 shrink-0 items-center justify-center
                       text-text-muted"
          >
            <IconChevronLeft size={20} />
          </button>

          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-2">
              <b className="truncate text-[16px] font-bold text-text">
                Quote {quote.reference}
              </b>
              <QuoteChip quote={quote} />
            </span>
            <span className="block truncate text-[12px] text-text-muted">{sub}</span>
          </span>

          <button
            type="button"
            aria-label="Preview the proposal"
            onClick={() => openTab('proposal')}
            className="flex h-9 w-9 shrink-0 items-center justify-center text-text-muted"
          >
            <IconEye size={18} />
          </button>
          {/* §1: ⋯ right of the eye — the secondary and the rest. */}
          <StageMore items={phoneStageItems(stage)} phone />
        </header>
      )
      : (
    <PageBar
      title={`Quote ${quote.reference}`}
      breadcrumbs={[
        { label: 'Jobs', onClick: () => navigate('/jobs') },
        { label: quote.jobReference, onClick: () => navigate(`/jobs/${quote.jobId}`) },
      ]}
      /*
       * Board 2: the tag beside the title, then who and where — so the sub
       * rides in the status slot, after the tag, rather than in `sub`,
       * which the bar draws before it.
       */
      status={(
        <>
          <QuoteChip quote={quote} />
          <span className="min-w-0 text-[12.5px] text-text-muted">{sub}</span>
        </>
      )}
      activeTab={tab}
      onTabChange={(key) => openTab(key as Tab)}
      actions={<StageBar stage={stage} blocking={blocking} busy={busy} />}
      tabs={(['build', 'billing', 'proposal', 'activity'] as const).map((key) => ({
        key,
        label: key[0]!.toUpperCase() + key.slice(1),
      }))}
    />
      )}
    </>
  )
}
